import { useState } from "react";
import { formatStorePrice, getProductPricing } from "../utils";
import { useDispatch } from "react-redux";
import toast from "react-hot-toast";
import {
  decrementBundleQuantity,
  getCartBundleAvailableQuantity,
  getCartBundleQuantity,
  incrementBundleQuantity,
  isCollectionCartProduct,
  normalizeAvailableQuantity,
} from "../store/cartSlice";
import { getCartBundlePricing } from "../utils/cartBundle";

const getProductImage = (product) =>
  product?.image || product?.image_url || product?.main_images?.[0] || "";

const CartBundleCard = ({
  bundle,
  onViewProduct,
  onRemove,
  onVariantChange,
  showRemove = false,
  compact = false,
}) => {
  const pricing = getCartBundlePricing(bundle);
  const dispatch = useDispatch();
  const bundleQuantity = getCartBundleQuantity(bundle);
  const availableBundleQuantity = getCartBundleAvailableQuantity(bundle);
  const hasKnownBundleStock = availableBundleQuantity !== null;
  const [editingProductId, setEditingProductId] = useState(null);  
  const hasReachedBundleStockLimit =
    hasKnownBundleStock && bundleQuantity >= availableBundleQuantity;

  const handleIncrementBundle = () => {
    if (hasReachedBundleStockLimit) {
      toast.error(
        "Maximum bundle quantity reached.",
      );
      return;
    }
    dispatch(incrementBundleQuantity(bundle.instanceId || bundle.id));
  };
  return (
    <div className="overflow-hidden rounded-[6px] border border-[#D8D3CC] bg-white">
      {/* BUNDLE HEADER */}
      <div className="border-b border-[#DDD8D1] bg-[#F7F4EF] px-4 py-4">
        <div className="flex items-start justify-between gap-4">
          <div>
            <span className="inline-flex rounded-[3px] bg-black px-2 py-1 font-spaceMono text-[8px] font-bold uppercase tracking-[0.16em] text-white">
              Bundle
            </span>

            <h3 className="mt-2 font-helvena text-[14px] font-medium uppercase text-[#111111]">
              {bundle?.name || "Collection Bundle"}
            </h3>

            <p className="mt-1 font-spaceMono text-[9px] uppercase tracking-[0.08em] text-[#777777]">
              {bundle?.items?.length || 0} products
              {bundle?.discountPercent
                ? ` • ${bundle.discountPercent}% bundle savings`
                : ""}
            </p>
          </div>
          <div className="flex shrink-0 flex-col items-end gap-2">
            <div className="flex items-center gap-2">
              <span className="font-spaceMono text-[9px] uppercase text-[#777777]">
                Qty
              </span>
              <div className="flex h-[28px] items-center overflow-hidden rounded-[4px] border border-[#CBC7C1] bg-white">
                <button
                  type="button"
                  disabled={bundleQuantity <= 1}
                  onClick={() => dispatch(decrementBundleQuantity(bundle.instanceId || bundle.id))}
                  className="flex h-full w-[30px] items-center justify-center text-[16px] disabled:cursor-not-allowed disabled:opacity-30"
                  aria-label="Decrease bundle quantity"
                >
                  −
                </button>
                <span className="min-w-[30px] border-x border-[#CBC7C1] px-2 text-center font-spaceMono text-[11px]">
                  {bundleQuantity}
                </span>
                <button
                  type="button"
                  disabled={hasReachedBundleStockLimit}
                  onClick={handleIncrementBundle}
                  className="flex h-full w-[30px] items-center justify-center text-[16px] disabled:cursor-not-allowed disabled:opacity-30"
                  aria-label="Increase bundle quantity"
                >
                  +
                </button>
              </div>
            </div>
            {showRemove ? (
              <button
                type="button"
                onClick={onRemove}
                className="font-helvena text-[11px] text-[#A34848] underline underline-offset-2"
              >
                Remove Bundle
              </button>
            ) : null}
          </div>
        </div>
      </div>
      {/* PRODUCTS INSIDE BUNDLE */}
      <div className="divide-y divide-[#E7E3DD]">
        {(bundle?.items || []).map((item) => {
          const product = item?.product || {};
          const stableProductId = product?.product_id ?? product?.id;
          const variants = Array.isArray(product?.variants)
              ? product.variants.filter(
                  (variant) =>
                    Number.isFinite(Number(variant?.id)) &&
                    Number(variant.id) > 0,
                )
              : [];
          const currentVariantId = Number(product?.variant_id ?? product?.id);
          const canEditVariant = Boolean(onVariantChange) && variants.length > 1;
          const isEditingVariant = String(editingProductId) === String(stableProductId);
          const quantity = Number(item?.quantity || 1);
          const productPricing = getProductPricing(
            product,
            isCollectionCartProduct(product),
          );
          const image = getProductImage(product);
          return (
            <div
              key={`${product.id}-${product.size || ""}`}
              className={["flex gap-3 px-4", compact ? "py-3" : "py-4"].join(
                " ",
              )}
            >
              <div
                className={[
                  "shrink-0 overflow-hidden rounded-[4px] bg-[#E8E5DF]",
                  compact ? "h-[72px] w-[56px]" : "h-[88px] w-[68px]",
                ].join(" ")}
              >
                {image ? (
                  <img
                    src={image}
                    alt={product?.name || "Product"}
                    className="h-full w-full object-cover"
                  />
                ) : null}
              </div>
              <div className="min-w-0 flex-1">
                <div className="flex justify-between gap-3">
                  <div className="min-w-0">
                    <p className="truncate font-helvena text-[12px] font-medium uppercase text-black">
                      {product?.name || "Product"}
                    </p>

                    <div className="mt-2">
                      <div className="flex items-start justify-between gap-2">
                        <div className="flex flex-wrap gap-1.5">
                      {product?.size ? (
                        <span className="rounded-[3px] bg-[#E7F0FF] px-2 py-1 font-spaceMono text-[9px] text-[#3D648B]">
                          Size: {product.size}
                        </span>
                      ) : null}

                      {product?.color ? (
                        <span className="rounded-[3px] bg-[#E7F0FF] px-2 py-1 font-spaceMono text-[9px] text-[#3D648B]">
                          Colour: {product.color}
                        </span>
                      ) : null}
                    </div>
                  </div>
                  {isEditingVariant ? (
                    <div className="mt-2 rounded-[4px] border border-[#E0DDD7] bg-[#FAFAF8] p-2">
                      <p className="mb-2 font-spaceMono text-[8px] uppercase tracking-[0.08em] text-[#777777]">
                        Select Variant
                      </p>

                      <div className="flex flex-wrap gap-1.5">
                        {variants.map((variant) => {
                          const variantId = Number(variant.id);
                          const available = normalizeAvailableQuantity(variant?.quantity);
                          const isSelected = variantId === currentVariantId;
                          const unavailable = variant?.isOutOfStock === true ||
                            (available !== null && available < bundleQuantity);

                          return (
                            <button
                              key={variantId}
                              type="button"
                              disabled={isSelected || unavailable}
                              onClick={() => {
                                onVariantChange?.(product,variant);
                                setEditingProductId(null);
                              }}
                              className={[
                                "rounded-[4px] border px-2 py-1.5",
                                "font-spaceMono text-[9px] transition-colors",
                                isSelected
                                  ? "border-black bg-black text-white"
                                  : "border-[#D8D5D0] bg-white text-[#313131]",
                                unavailable
                                  ? "cursor-not-allowed opacity-35"
                                  : !isSelected
                                    ? "hover:border-black"
                                    : "",
                              ].join(" ")}
                            >
                              {variant?.size || `#${variantId}`}
                              {variant?.color ? ` / ${variant.color}` : ""}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  ) : null}
                </div>
                    <p className="mt-2 font-spaceMono text-[9px] text-[#777777]">
                      Qty: {quantity}
                    </p>
                  </div>

                  <p className="shrink-0 font-helvena text-[13px] text-[#525252]">
                    {formatStorePrice(
                      productPricing.discountedPrice * quantity,
                    )}
                  </p>
                </div>

              <div className="mt-2 flex items-center gap-3">
                {onViewProduct ? (
                  <button
                    type="button"
                    onClick={() => onViewProduct(product)}
                    className="font-helvena text-[10px] text-[#595973] underline underline-offset-2"
                  >
                    View Product
                  </button>
                ) : null}
                {canEditVariant ? (
                  <>
                    {onViewProduct ? (
                      <span className="text-[10px] text-[#B8B8B8]">|</span>
                    ) : null}
                    <button
                      type="button"
                      onClick={() =>
                        setEditingProductId(isEditingVariant ? null : stableProductId,
                      )}
                      className="font-helvena text-[10px] text-[#595973] underline underline-offset-2">
                      {isEditingVariant ? "Cancel" : "Edit"}
                    </button>
                  </>
                ) : null}
              </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* BUNDLE TOTAL */}
      <div className="border-t border-[#DDD8D1] bg-[#FAF9F7] px-4 py-4">
        <div className="flex justify-between font-spaceMono text-[10px] uppercase text-[#6A7282]">
          <span>Products subtotal</span>
          <span>{formatStorePrice(pricing.currentSubtotal)}</span>
        </div>

        {pricing.bundleDiscount > 0 ? (
          <div className="mt-2 flex justify-between font-spaceMono text-[10px] font-semibold uppercase text-[#257A45]">
            <span>Bundle savings ({bundle.discountPercent}%)</span>
            <span>-{formatStorePrice(pricing.bundleDiscount)}</span>
          </div>
        ) : null}

        <div className="mt-3 flex justify-between border-t border-[#DDD8D1] pt-3 font-spaceMono text-[11px] font-bold uppercase text-[#313131]">
          <span>Bundle Total</span>
          <span>{formatStorePrice(pricing.total)}</span>
        </div>
      </div>
    </div>
  );
};

export default CartBundleCard;
